/**
 * OnboardingActionRenderer - Renderer for onboarding action types.
 *
 * Renders actions that suggest team members to invite during onboarding.
 * Supports all render modes with appropriate display.
 */

import { Mail01 } from '@untitledui/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Table, TableCard } from '@/lib/base/table';
import { logger } from '@/lib/logger';

import { Avatar } from '../../../lib/base/avatar/avatar';
import {
  getAvatarInitials,
  getGravatarUrl,
} from '../../../lib/crm/utils/avatar';
import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ActionPayload,
  OnboardingActionPayload,
  TeamSuggestion,
} from '../../../lib/proposals/core/types';
import { isOnboardingActionPayload } from '../../../lib/proposals/core/types';
import { TeamIcon } from '../../../lib/proposals/registry/icons';
import type { ActionRendererProps } from '../../../lib/proposals/registry/types';
import {
  ActionError,
  ProposalContainer,
  ProposalInfoCallout,
  useAutoSaveCoordinationOptional,
} from '../shared';

const PAID_PLAN_SEAT_NOTE =
  "If your team is on a paid plan, we'll automatically add seats as needed when you invite – extra seats are billed prorated.";

/**
 * Extract onboarding payload data, returning defaults for invalid payloads
 */
function extractPayloadData(payload: ActionPayload) {
  if (isOnboardingActionPayload(payload)) {
    const onboardingPayload = payload as OnboardingActionPayload;
    return {
      isValid: true,
      title: onboardingPayload.action_params.title,
      description: onboardingPayload.action_params.description,
      suggestions: onboardingPayload.action_params.suggestions || [],
      selected_emails: onboardingPayload.action_params.selected_emails,
      payload: onboardingPayload,
    };
  }
  return {
    isValid: false,
    title: '',
    description: '',
    suggestions: [] as TeamSuggestion[],
    selected_emails: undefined as string[] | undefined,
    payload: null,
  };
}

/**
 * Shared invite section component - displays info callout, suggestions table, and manual email input.
 * Used in both the meet_your_team (admin) and invite_team flows.
 */
interface InviteSectionProps {
  suggestions: TeamSuggestion[];
  selectedSet: Set<string>;
  onToggle: (email: string) => void;
  onSetSelection: (emails: string[]) => void;
  manualEmail: string;
  setManualEmail: (email: string) => void;
  manualEmailError: string;
  setManualEmailError: (error: string) => void;
  handleAddManualEmail: () => void;
}

function InviteSection({
  suggestions,
  selectedSet,
  onToggle,
  onSetSelection,
  manualEmail,
  setManualEmail,
  manualEmailError,
  setManualEmailError,
  handleAddManualEmail,
}: InviteSectionProps) {
  return (
    <>
      <ProposalInfoCallout>
        Team members will get access to shared contacts, deals and can
        collaborate with you in Ahoy. You can adjust the permissions later.{' '}
        {PAID_PLAN_SEAT_NOTE}
      </ProposalInfoCallout>

      {/* UntitledUI Table */}
      <TeamSuggestionsTable
        suggestions={suggestions}
        selectedSet={selectedSet}
        onToggle={onToggle}
        onSetSelection={onSetSelection}
      />

      {/* Manual email input section */}
      <div className='rounded-lg border border-primary bg-primary p-4 mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <p
          className='text-base font-semibold shrink-0'
          style={{ color: 'var(--color-text-primary)' }}
        >
          Did we forget someone? Add them manually
        </p>
        <div className='min-w-0'>
          <div className='flex items-center gap-2'>
            <div
              className='flex items-center gap-2 border border-primary rounded-lg px-3 py-2 min-w-0 flex-1 sm:w-[280px] sm:flex-initial focus-within:outline-2 focus-within:outline-offset-2'
              style={{ outlineColor: 'var(--color-brand-600)' }}
            >
              <label htmlFor='manual-email' className='sr-only'>
                Add team member email address
              </label>
              <Mail01
                className='w-5 h-5'
                style={{ color: 'var(--color-text-tertiary)' }}
                aria-hidden='true'
              />
              <input
                id='manual-email'
                type='email'
                placeholder='colleague@company.com'
                value={manualEmail}
                onChange={e => {
                  setManualEmail(e.target.value);
                  setManualEmailError('');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddManualEmail();
                  }
                }}
                className='flex-1 outline-none bg-transparent text-sm'
                style={{ color: 'var(--color-text-primary)' }}
                aria-describedby={manualEmailError ? 'email-error' : undefined}
              />
            </div>
            <Button
              color='primary'
              size='sm'
              onClick={handleAddManualEmail}
              isDisabled={!manualEmail.trim()}
            >
              Add
            </Button>
          </div>
          {manualEmailError && (
            <p
              id='email-error'
              className='text-xs mt-1'
              style={{ color: 'var(--color-text-error-primary)' }}
              role='alert'
            >
              {manualEmailError}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Renderer for onboarding action types.
 * Supports all render modes with appropriate display.
 */
export function OnboardingActionRenderer({
  action,
  mode,
  className = '',
  onUpdate,
  proposalId,
  orgId,
}: ActionRendererProps) {
  // Extract payload data - always returns valid object shape
  const payloadData = useMemo(
    () => extractPayloadData(action.payload),
    [action.payload]
  );
  const { isValid, title, description, suggestions, selected_emails, payload } =
    payloadData;

  // Create proposal client for persisting payload updates to backend
  const proposalClient = useMemo(
    () => (orgId ? createProposalClient(orgId) : null),
    [orgId]
  );

  // Each successful update_payload past the versioning window mints a NEW
  // action version (new id) and marks the old row `is_current_version=False`;
  // the backend's `get_action` only resolves current versions, so reusing the
  // old id makes the NEXT save fail with "Action {id} not found". Track the
  // current id and adopt the server's returned action after every save. This
  // matters most on the retry/queued-update paths below, which re-enter the
  // save with an id captured before the first attempt.
  const actionIdRef = useRef(action.id);
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // Track in-flight save to prevent concurrent user-triggered API calls
  const isSavingRef = useRef(false);
  // Queue pending updates that arrive while a save is in flight
  const pendingUpdateRef = useRef<string[] | null>(null);
  // Track retry count to prevent infinite retry loops
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  // Track initial persist in-flight separately to avoid blocking manual email adds
  const isInitialPersistInFlightRef = useRef(false);

  // Autosave coordination for initial persist (optional - may be null outside proposal context)
  const coordination = useAutoSaveCoordinationOptional();

  // Get org members to filter out already-invited team members
  const { members: orgMembers } = useOrgMembers(orgId);
  const memberEmails = useMemo(
    () => new Set(orgMembers.map(m => m.email.toLowerCase())),
    [orgMembers]
  );

  // Track initial persist state for coordination
  const initialPersistPromiseRef = useRef<Promise<void> | null>(null);
  const hasAttemptedInitialPersistRef = useRef(false);
  const initialPersistSucceededRef = useRef(false);

  // Initialize selected emails from payload or default to all suggestions
  // Note: We distinguish between undefined (no selection yet -> default to all)
  // and empty array (user explicitly deselected all -> keep empty)
  const [selectedSet, setSelectedSet] = useState<Set<string>>(() => {
    if (selected_emails !== undefined) {
      // User has made a selection (could be empty array for "deselect all")
      return new Set(selected_emails);
    }
    // No selection made yet, default to all suggestions
    return new Set(suggestions.map(s => s.email));
  });

  // State for manual email input
  const [manualEmail, setManualEmail] = useState('');
  const [manualEmailError, setManualEmailError] = useState('');

  // Sync with payload changes
  useEffect(() => {
    if (selected_emails !== undefined) {
      // User has made a selection (could be empty array for "deselect all")
      setSelectedSet(new Set(selected_emails));
    } else {
      // No selection made yet, default to all suggestions
      setSelectedSet(new Set(suggestions.map(s => s.email)));
    }
  }, [selected_emails, suggestions]);

  // Persist selection to backend via updateActionPayload API
  // This ensures the encrypted payload in the database reflects the user's selection
  // so that when the proposal is approved, the executor uses the correct selected_emails.
  // If a save is already in flight, queue the update to prevent data loss from rapid toggles.
  const persistSelection = useCallback(
    async (newEmails: string[], currentPayload: OnboardingActionPayload) => {
      if (!proposalClient || !proposalId) {
        return;
      }

      // If already saving (user toggle) or initial persist is in-flight, queue this update.
      // Without this check, user updates during initial persist could be overwritten if
      // the initial persist API call completes after the user's update.
      if (isSavingRef.current || isInitialPersistInFlightRef.current) {
        pendingUpdateRef.current = newEmails;
        return;
      }

      isSavingRef.current = true;
      try {
        const updatedPayload: OnboardingActionPayload = {
          action_params: {
            ...currentPayload.action_params,
            selected_emails: newEmails,
          },
        };
        const saved = await proposalClient.updateActionPayload(
          proposalId,
          actionIdRef.current,
          { payload: updatedPayload }
        );
        // Adopt the server's row so the next save (including the queued and
        // retry paths below) targets the current version.
        actionIdRef.current = saved.id;

        // Reset retry count on successful save
        retryCountRef.current = 0;

        // After save completes, process any pending update
        if (pendingUpdateRef.current !== null) {
          const pending = pendingUpdateRef.current;
          pendingUpdateRef.current = null;
          isSavingRef.current = false;
          // Recursively persist the pending update
          await persistSelection(pending, currentPayload);
          return;
        }

        // Clear pending update on success
        pendingUpdateRef.current = null;
      } catch (error) {
        logger.error('Failed to persist selection', error as Error, {
          component: 'OnboardingActionRenderer',
        });

        // Determine what to retry: pending update takes priority over current update
        const retryEmails =
          pendingUpdateRef.current !== null
            ? pendingUpdateRef.current
            : newEmails;
        pendingUpdateRef.current = null;

        // Retry with exponential backoff if under retry limit
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          const delay = Math.pow(2, retryCountRef.current - 1) * 1000; // 1s, 2s, 4s
          logger.debug(`Retrying persist in ${delay}ms`, {
            attempt: retryCountRef.current,
            maxRetries: MAX_RETRIES,
          });

          // Keep in-flight guard set during backoff to prevent race conditions
          await new Promise(resolve => setTimeout(resolve, delay));
          // Guard still set, retry will happen with guard held
          isSavingRef.current = false;
          await persistSelection(retryEmails, currentPayload);
          return;
        }

        // Max retries exhausted - reset counter and throw to signal failure
        retryCountRef.current = 0;
        throw new Error('Max retries exhausted, selection could not be saved');
      } finally {
        isSavingRef.current = false;
      }
    },
    // Deliberately not depending on `action.id`: the save target lives in
    // `actionIdRef`, so this callback stays stable across version bumps.
    [proposalClient, proposalId]
  );

  // Capture default emails for initial persist (avoids stale closure in effect)
  const defaultEmailsRef = useRef<string[]>([]);
  if (selected_emails === undefined && suggestions.length > 0) {
    defaultEmailsRef.current = suggestions.map(s => s.email);
  }

  // Initial persist effect with autosave coordination
  // Consolidates: registration, flush registration, initial persist, and cleanup in one effect
  // to avoid race conditions between separate effects.
  // Registration is done inside the effect (not useMemo) so React's cleanup guarantees
  // correct lifecycle management in Strict Mode and concurrent features.
  useEffect(() => {
    // Guard conditions for persist - check all before starting async work
    const shouldPersist =
      !hasAttemptedInitialPersistRef.current &&
      selected_emails === undefined &&
      defaultEmailsRef.current.length > 0 &&
      proposalClient !== null &&
      proposalId !== undefined &&
      payload !== null;

    if (!shouldPersist) {
      // No initial persist needed; register with coordination if available
      if (coordination) {
        const coordinationHandle = coordination.register(
          `onboarding-${action.id}`
        );
        const { setFlushFn, unregister } = coordinationHandle;
        // Check initial persist state: if it was attempted and failed, block approval.
        // This handles the case where the effect re-runs after a failed initial persist
        // (e.g., due to a dependency change) — the no-op must not silently clear the failure.
        setFlushFn(async () => {
          if (
            hasAttemptedInitialPersistRef.current &&
            !initialPersistSucceededRef.current
          ) {
            throw new Error('Initial persist failed');
          }
        });
        return () => {
          unregister();
        };
      }
      return;
    }

    // Mark as attempted BEFORE starting persist (prevents duplicate attempts)
    hasAttemptedInitialPersistRef.current = true;

    // Capture emails from ref to use in async operation
    const emailsToSave = defaultEmailsRef.current;

    // Helper to do the actual initial persist API call directly
    // (does not use persistSelection to avoid setting isSavingRef, which would
    //  block handleAddManualEmail when the initial persist is in-flight)
    const doInitialPersist = async () => {
      // proposalClient and proposalId are guaranteed non-null here (checked in shouldPersist above)
      if (!proposalClient || proposalId === undefined) {
        return;
      }
      isInitialPersistInFlightRef.current = true;
      try {
        const updatedPayload: OnboardingActionPayload = {
          action_params: {
            ...payload.action_params,
            selected_emails: emailsToSave,
          },
        };
        const saved = await proposalClient.updateActionPayload(
          proposalId,
          actionIdRef.current,
          { payload: updatedPayload }
        );
        // Adopt the server's row — the queued persistSelection below would
        // otherwise re-save against an id this call just superseded.
        actionIdRef.current = saved.id;
        initialPersistSucceededRef.current = true;
      } finally {
        isInitialPersistInFlightRef.current = false;
        // Only process queued user updates if the initial persist succeeded.
        // If it failed we must NOT run persistSelection because:
        // 1. initialPersistSucceededRef.current must remain false so the flush
        //    function correctly blocks approval (throwing 'Initial persist failed').
        // 2. Throwing from a finally block replaces the original exception, masking
        //    the root-cause error from the caller.
        if (pendingUpdateRef.current !== null) {
          const pending = pendingUpdateRef.current;
          pendingUpdateRef.current = null;
          if (initialPersistSucceededRef.current) {
            // Use payload from the current closure; persistSelection handles its own guard.
            // Wrap in try/catch: persistSelection can throw on max retries exhausted.
            // A throw here (inside finally) would propagate to the caller and corrupt
            // initialPersistSucceededRef by triggering the outer catch block.
            try {
              await persistSelection(pending, payload);
            } catch (pendingError) {
              console.error(
                '[OnboardingActionRenderer] Queued update persist failed:',
                pendingError
              );
            }
          }
          // If initial persist failed, the pending update is discarded.
        }
      }
    };

    if (coordination) {
      // Register inside the effect so cleanup can always unregister the same handle
      const coordinationHandle = coordination.register(
        `onboarding-${action.id}`
      );
      const { setSaveState, setFlushFn, unregister } = coordinationHandle;

      // Register flush function FIRST (before any async work)
      // This ensures StickyFooter can always wait for us, even if approval
      // is clicked immediately after mount
      setFlushFn(async () => {
        // Wait for initial persist promise if it exists
        if (initialPersistPromiseRef.current) {
          try {
            await initialPersistPromiseRef.current;
          } catch {
            // Error already handled in the persist logic
          }
        }
        // Check if persist actually succeeded - if not, throw to signal error
        // This ensures StickyFooter sees the failure even if promise resolved
        if (
          hasAttemptedInitialPersistRef.current &&
          !initialPersistSucceededRef.current
        ) {
          throw new Error('Initial persist failed');
        }
      });

      setSaveState('saving');

      // Create persist promise and assign to ref BEFORE any async work
      // This ensures flush function can always find the promise
      const persistPromise = (async () => {
        try {
          await doInitialPersist();
          setSaveState('idle');
        } catch (error) {
          // On failure, signal error state so StickyFooter blocks approval
          initialPersistSucceededRef.current = false;
          setSaveState('error');
          console.error(
            '[OnboardingActionRenderer] Initial persist failed:',
            error
          );
          // Do not re-throw: failure is tracked via initialPersistSucceededRef.
          // The flush function checks that ref to signal failure to StickyFooter.
        }
      })();

      // Assign to ref immediately (before promise settles).
      // Add a no-op catch to prevent unhandled promise rejection if the stored
      // promise reference is awaited in a context that doesn't catch.
      initialPersistPromiseRef.current = persistPromise;
      persistPromise.catch(() => {});

      // Cleanup: unregister from coordination on unmount
      return () => {
        unregister();
      };
    } else {
      // No coordination context: still persist defaults so payload is complete
      const persistPromise = (async () => {
        try {
          await doInitialPersist();
        } catch (error) {
          initialPersistSucceededRef.current = false;
          console.error(
            '[OnboardingActionRenderer] Initial persist failed (no coordination):',
            error
          );
        }
      })();

      initialPersistPromiseRef.current = persistPromise;
      persistPromise.catch(() => {});
    }
  }, [
    coordination,
    action.id,
    selected_emails,
    proposalClient,
    proposalId,
    payload,
    persistSelection,
  ]);

  // Validate payload type - after hooks
  if (!isValid || !payload) {
    return (
      <ActionError message='Invalid onboarding action payload' mode={mode} />
    );
  }

  const handleAddManualEmail = async () => {
    // Reset previous error
    setManualEmailError('');

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualEmail.trim())) {
      setManualEmailError('Please enter a valid email address');
      return;
    }

    const normalizedEmail = manualEmail.trim().toLowerCase();

    // Check for duplicates (case-insensitive)
    const isDuplicate = suggestions.some(
      s => s.email.toLowerCase() === normalizedEmail
    );
    if (isDuplicate) {
      setManualEmailError('This email is already in the list');
      return;
    }

    // Check if email belongs to an existing org member
    if (memberEmails.has(normalizedEmail)) {
      setManualEmailError('This user is already a team member');
      return;
    }

    // Check if a save is already in flight - prevent concurrent updates
    if (isSavingRef.current || isInitialPersistInFlightRef.current) {
      setManualEmailError('Please wait for the previous operation to complete');
      return;
    }

    // Derive name from email prefix (title-cased, dots/underscores → spaces)
    const emailPrefix = normalizedEmail.split('@')[0];
    const derivedName = emailPrefix
      .replace(/[._]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Check domain match against first suggestion
    const firstDomain =
      suggestions.length > 0 ? suggestions[0].email.split('@')[1] : '';
    const manualDomain = normalizedEmail.split('@')[1];
    const domainMatch = firstDomain === manualDomain;

    // Create new suggestion
    const newSuggestion: TeamSuggestion = {
      email: normalizedEmail,
      name: derivedName,
      domain_match: domainMatch,
      total_interactions: 0,
      is_manual: true,
    };

    // Create updated payload with new suggestion and auto-selected email
    const updatedSuggestions = [...suggestions, newSuggestion];
    const updatedSelectedEmails = [...Array.from(selectedSet), normalizedEmail];

    const updatedPayload: OnboardingActionPayload = {
      action_params: {
        ...payload.action_params,
        suggestions: updatedSuggestions,
        selected_emails: updatedSelectedEmails,
      },
    };

    // Store previous payload for rollback
    const previousPayload = payload;

    // Optimistic local update
    if (onUpdate) {
      onUpdate({
        ...action,
        payload: updatedPayload,
      });
    }

    // Update local state optimistically
    setSelectedSet(new Set(updatedSelectedEmails));

    // Set in-flight guard to prevent race conditions with persistSelection
    isSavingRef.current = true;

    // Persist to backend
    try {
      if (proposalClient && proposalId) {
        const saved = await proposalClient.updateActionPayload(
          proposalId,
          actionIdRef.current,
          { payload: updatedPayload }
        );
        // Adopt the server's row so the next save targets the current version.
        actionIdRef.current = saved.id;
        onUpdate?.(saved);
      }

      // Success: clear input and error
      setManualEmail('');
      setManualEmailError('');
    } catch (error) {
      console.error(
        '[OnboardingActionRenderer] Failed to add manual email:',
        error
      );

      // Rollback optimistic update
      if (onUpdate) {
        onUpdate({
          ...action,
          payload: previousPayload,
        });
      }
      setSelectedSet(new Set(selected_emails || suggestions.map(s => s.email)));

      // Show error toast
      toast.error("We couldn't add that team member. Try again.");
    } finally {
      // Release in-flight guard
      isSavingRef.current = false;
    }
  };

  const handleToggle = (email: string) => {
    const previousSet = new Set(selectedSet);
    const newSet = new Set(selectedSet);
    if (newSet.has(email)) {
      newSet.delete(email);
    } else {
      newSet.add(email);
    }
    setSelectedSet(newSet);

    const newEmails = Array.from(newSet);

    // Optimistic local update
    if (onUpdate) {
      const updatedPayload: OnboardingActionPayload = {
        action_params: {
          ...payload.action_params,
          selected_emails: newEmails,
        },
      };
      onUpdate({
        ...action,
        payload: updatedPayload,
      });
    }

    // Persist to backend so executor has correct selected_emails
    persistSelection(newEmails, payload).catch(error => {
      console.error(
        '[OnboardingActionRenderer] Failed to persist toggle selection:',
        error
      );

      // Rollback optimistic update
      setSelectedSet(previousSet);
      if (onUpdate) {
        onUpdate({ ...action, payload });
      }
      toast.error("We couldn't update that selection. Try again.");
    });
  };

  // Batch selection update (for select all / deselect all)
  const handleSetSelection = (emails: string[]) => {
    const previousSet = new Set(selectedSet);
    const newSet = new Set(emails);
    setSelectedSet(newSet);

    // Optimistic local update
    if (onUpdate) {
      const updatedPayload: OnboardingActionPayload = {
        action_params: {
          ...payload.action_params,
          selected_emails: emails,
        },
      };
      onUpdate({
        ...action,
        payload: updatedPayload,
      });
    }

    // Persist to backend so executor has correct selected_emails
    persistSelection(emails, payload).catch(error => {
      console.error(
        '[OnboardingActionRenderer] Failed to persist batch selection:',
        error
      );

      // Rollback optimistic update
      setSelectedSet(previousSet);
      if (onUpdate) {
        onUpdate({ ...action, payload });
      }
      toast.error("We couldn't update that selection. Try again.");
    });
  };

  // Inline mode - text only
  if (mode === 'inline') {
    return <span className={className}>{title}</span>;
  }

  // Compact mode - icon and short summary
  if (mode === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <TeamIcon className='w-4 h-4 shrink-0 text-fg-secondary' />
        <span className='text-sm truncate'>{title}</span>
      </div>
    );
  }

  // Header mode - for accordion headers
  if (mode === 'header') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <TeamIcon className='w-5 h-5 text-fg-secondary' />
        <span className='font-medium text-fg-primary truncate'>{title}</span>
        {/* bg/text-primary-100/700 didn't exist in theme.css — badge had no bg */}
        <span className='text-xs px-2 py-0.5 rounded-full bg-utility-gray-100 text-utility-gray-700'>
          Onboarding
        </span>
      </div>
    );
  }

  // Readonly mode - post-approval success state
  if (mode === 'readonly') {
    // Check execution_result for status information
    const executionResult = action.execution_result;

    // Handle full success (invitations)
    if (executionResult && executionResult.status === 'success') {
      const invitedCount =
        typeof executionResult.invited_count === 'number'
          ? executionResult.invited_count
          : selectedSet.size;
      return (
        <div className={`space-y-4 ${className}`}>
          <div className='flex items-start gap-3'>
            <TeamIcon className='w-5 h-5 text-utility-success-500 shrink-0 mt-1' />
            <div className='flex-1 min-w-0'>
              <h3
                className='text-lg font-semibold'
                style={{ color: 'var(--color-text-primary)' }}
              >
                {title}
              </h3>
              <p
                className='text-sm mt-1'
                style={{ color: 'var(--color-text-success-primary)' }}
              >
                {invitedCount} invitation{invitedCount === 1 ? '' : 's'} sent
                successfully
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Handle partial success with detailed failure information
    if (executionResult && executionResult.status === 'partial_success') {
      const invitedCount =
        typeof executionResult.invited_count === 'number'
          ? executionResult.invited_count
          : 0;
      const errorCount =
        typeof executionResult.error_count === 'number'
          ? executionResult.error_count
          : 0;
      return (
        <div className={`space-y-4 ${className}`}>
          <div className='flex items-start gap-3'>
            <TeamIcon className='w-5 h-5 shrink-0 mt-1 text-utility-warning-500' />
            <div className='flex-1 min-w-0'>
              <h3
                className='text-lg font-semibold'
                style={{ color: 'var(--color-text-primary)' }}
              >
                {title}
              </h3>
              <p
                className='text-sm mt-1'
                style={{ color: 'var(--color-text-warning-primary)' }}
              >
                {invitedCount} invitation{invitedCount === 1 ? '' : 's'} sent,{' '}
                {errorCount} failed
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (executionResult && executionResult.status === 'failure') {
      const errors = Array.isArray(executionResult.errors)
        ? executionResult.errors
        : [];
      if (errors.length > 0) {
        logger.error(
          '[OnboardingActionRenderer] invitation errors',
          undefined,
          { component: 'proposals', errors }
        );
      }
      return (
        <div className={`space-y-4 ${className}`}>
          <div className='flex items-start gap-3'>
            <TeamIcon className='w-5 h-5 shrink-0 mt-1 text-utility-error-500' />
            <div className='flex-1 min-w-0'>
              <h3
                className='text-lg font-semibold'
                style={{ color: 'var(--color-text-primary)' }}
              >
                {title}
              </h3>
              <p
                className='text-sm mt-1'
                style={{ color: 'var(--color-text-error-primary)' }}
              >
                We couldn't send invitations
              </p>
              {errors.length > 0 && (
                <ul
                  className='text-sm mt-2 space-y-1'
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {errors.map((err: { email: string; error: string }) => (
                    <li key={err.email}>
                      {err.email}: We couldn't send this invitation.
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Fallback: show suggestions in read-only format
    return (
      <div className={`space-y-4 ${className}`}>
        <div className='flex items-start gap-3'>
          <TeamIcon className='w-5 h-5 shrink-0 mt-1 text-fg-brand-primary' />
          <div className='flex-1 min-w-0'>
            <h3
              className='text-lg font-semibold'
              style={{ color: 'var(--color-text-primary)' }}
            >
              {title}
            </h3>
          </div>
        </div>
        <p className='text-sm' style={{ color: 'var(--color-text-secondary)' }}>
          {description}
        </p>
        <div className='space-y-2'>
          {suggestions.map(suggestion => (
            <SuggestionRow
              key={suggestion.email}
              suggestion={suggestion}
              isSelected={selectedSet.has(suggestion.email)}
              readonly
            />
          ))}
        </div>
      </div>
    );
  }

  // Extract action_key from params
  const actionKey = payload.action_params.action_key || 'invite_team';
  const existingMembers = payload.action_params.existing_members || [];
  const canInvite = payload.action_params.can_invite ?? false;

  // Full mode - complete preview with editing
  let actionContent: ReactNode;
  if (actionKey === 'meet_your_team') {
    actionContent = (
      <>
        {/* Your Team Section */}
        {existingMembers.length > 0 && (
          <div className='space-y-3'>
            <h3
              className='text-lg font-semibold'
              style={{ color: 'var(--color-text-primary)' }}
            >
              Your team
            </h3>
            <div className='space-y-2'>
              {existingMembers.map(member => (
                <div
                  key={member.id}
                  className='flex items-center gap-3 p-3 rounded-lg border border-primary bg-primary'
                >
                  <Avatar
                    size='md'
                    src={member.avatar_url || undefined}
                    initials={getAvatarInitials(member.name)}
                    alt={member.name}
                  />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span
                        className='font-medium text-sm'
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {member.name}
                      </span>
                      {member.is_inviter && (
                        <Badge color='brand' size='sm'>
                          Invited you
                        </Badge>
                      )}
                    </div>
                    <span
                      className='text-sm'
                      style={{ color: 'var(--color-text-tertiary)' }}
                    >
                      {member.email}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conditional: Show invite section if can_invite is true */}
        {canInvite && (
          <>
            <h3
              className='text-lg font-semibold'
              style={{ color: 'var(--color-text-primary)' }}
            >
              Invite more
            </h3>

            <InviteSection
              suggestions={suggestions}
              selectedSet={selectedSet}
              onToggle={handleToggle}
              onSetSelection={handleSetSelection}
              manualEmail={manualEmail}
              setManualEmail={setManualEmail}
              manualEmailError={manualEmailError}
              setManualEmailError={setManualEmailError}
              handleAddManualEmail={handleAddManualEmail}
            />
          </>
        )}
      </>
    );
  } else {
    actionContent = (
      <InviteSection
        suggestions={suggestions}
        selectedSet={selectedSet}
        onToggle={handleToggle}
        onSetSelection={handleSetSelection}
        manualEmail={manualEmail}
        setManualEmail={setManualEmail}
        manualEmailError={manualEmailError}
        setManualEmailError={setManualEmailError}
        handleAddManualEmail={handleAddManualEmail}
      />
    );
  }

  return (
    <ProposalContainer className={className}>{actionContent}</ProposalContainer>
  );
}

interface TeamSuggestionsTableProps {
  suggestions: TeamSuggestion[];
  selectedSet: Set<string>;
  onToggle: (email: string) => void;
  onSetSelection: (emails: string[]) => void;
}

function TeamSuggestionsTable({
  suggestions,
  selectedSet,
  onToggle,
  onSetSelection,
}: TeamSuggestionsTableProps) {
  // Convert Set to react-aria Selection format
  const selectedKeys = selectedSet;

  // Handle selection changes from react-aria
  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') {
      // Select all - batch update
      onSetSelection(suggestions.map(s => s.email));
    } else {
      const newKeys = keys as Set<string>;
      // Check if this is a single item toggle or a batch operation
      const changedCount = suggestions.filter(s => {
        const wasSelected = selectedSet.has(s.email);
        const isNowSelected = newKeys.has(s.email);
        return wasSelected !== isNowSelected;
      }).length;

      if (changedCount === 1) {
        // Single item toggle - use onToggle for optimistic update
        suggestions.forEach(s => {
          const wasSelected = selectedSet.has(s.email);
          const isNowSelected = newKeys.has(s.email);
          if (wasSelected !== isNowSelected) {
            onToggle(s.email);
          }
        });
      } else {
        // Batch operation (e.g., deselect all) - use onSetSelection
        onSetSelection(Array.from(newKeys) as string[]);
      }
    }
  };

  return (
    <TableCard.Root size='sm'>
      <div className='overflow-x-auto'>
        <Table
          aria-label='Team members to invite'
          size='sm'
          selectionMode='multiple'
          selectionBehavior='toggle'
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
        >
          <Table.Header bordered>
            {/* Avatar column - no label, narrow */}
            <Table.Head id='avatar' className='!w-12 !pr-0' />

            {/* Name column */}
            <Table.Head id='name' label='Name' isRowHeader />

            {/* Description column - no label */}
            <Table.Head id='description' />

            {/* Email address column */}
            <Table.Head id='email' label='Email address' />
          </Table.Header>

          <Table.Body items={suggestions.map(s => ({ ...s, id: s.email }))}>
            {suggestion => (
              <SuggestionTableRow
                key={suggestion.email}
                suggestion={suggestion}
              />
            )}
          </Table.Body>
        </Table>
      </div>
    </TableCard.Root>
  );
}

interface SuggestionTableRowProps {
  suggestion: TeamSuggestion;
}

function SuggestionTableRow({ suggestion }: SuggestionTableRowProps) {
  const { email, name, domain_match, total_interactions, job_title } =
    suggestion;
  const handle = `@${email.split('@')[0]}`;

  // Build description text
  let descriptionText = '';
  if (job_title) {
    const interactionSummary = `${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
    descriptionText = `${job_title} • ${interactionSummary}`;
  } else if (domain_match) {
    descriptionText = `Domain match • ${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
  } else {
    descriptionText = `${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
  }

  // Generate Gravatar URL (80px for md size avatar)
  const gravatarUrl = getGravatarUrl(email, 80);

  return (
    <Table.Row id={email}>
      {/* Avatar cell - narrow, no right padding */}
      <Table.Cell className='!w-12 !pr-0'>
        <Avatar
          size='md'
          src={gravatarUrl}
          initials={getAvatarInitials(name)}
          alt={name}
        />
      </Table.Cell>

      {/* Name cell */}
      <Table.Cell>
        <div className='flex flex-col min-w-0'>
          <span className='font-medium text-sm text-primary truncate'>
            {name}
          </span>
          <span className='text-sm text-tertiary truncate'>{handle}</span>
        </div>
      </Table.Cell>

      {/* Description cell */}
      <Table.Cell>
        <span className='text-sm text-tertiary truncate'>
          {descriptionText}
        </span>
      </Table.Cell>

      {/* Email cell */}
      <Table.Cell>
        <span className='text-sm text-tertiary truncate'>{email}</span>
      </Table.Cell>
    </Table.Row>
  );
}

// Legacy SuggestionRow for readonly mode (used in readonly view)
interface SuggestionRowProps {
  suggestion: TeamSuggestion;
  isSelected: boolean;
  readonly?: boolean;
}

function SuggestionRow({ suggestion, isSelected }: SuggestionRowProps) {
  const { email, name, domain_match, total_interactions, job_title } =
    suggestion;
  const handle = `@${email.split('@')[0]}`;

  // Build description text
  let descriptionText = '';
  if (job_title) {
    const interactionSummary = `${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
    descriptionText = `${job_title} • ${interactionSummary}`;
  } else if (domain_match) {
    descriptionText = `Domain match • ${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
  } else {
    descriptionText = `${total_interactions} interaction${total_interactions === 1 ? '' : 's'}`;
  }

  return (
    <div className='flex items-center gap-3 py-3 border-b border-secondary last:border-b-0'>
      {/* Avatar + Name + Handle */}
      <div className='w-[200px] flex-shrink-0 flex items-center gap-3'>
        <Avatar size='md' initials={getAvatarInitials(name)} />
        <div className='flex flex-col min-w-0'>
          <span className='font-medium text-sm text-primary truncate'>
            {name}
          </span>
          <span className='text-sm text-tertiary truncate'>{handle}</span>
        </div>
      </div>

      {/* Description */}
      <div className='flex-1 min-w-0'>
        <span className='text-sm text-tertiary truncate'>
          {descriptionText}
        </span>
      </div>

      {/* Email */}
      <div className='w-[220px] flex-shrink-0'>
        <span className='text-sm text-tertiary truncate'>{email}</span>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <span className='text-xs text-utility-success-600 font-medium'>
          Selected
        </span>
      )}
    </div>
  );
}
